import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  LinearProgress
} from '@mui/material'
import {
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material'
import GuardReportsInstitutionTab from './GuardReportsInstitutionTab'
import GuardReportsDoctorTab from './GuardReportsDoctorTab'
import SegmentReportsTab from './SegmentReportsTab'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line
} from 'recharts'
import { useAuth } from '../../contexts/AuthContext'
import { billingApi, institutionService, userService } from '../../services/api'
import {
  Institution,
  UserWithProfile,
  BillingReportInstitutionResponse,
  BillingReportInstitutionClosedMonth,
  BillingReportStatusAggregate,
  // BillingReportTimelinePoint,
  BillingReportDoctorResponse,
  BillingReportDoctorSummary,
  // BillingReportDoctorSegmentBreakdown,
  // BillingReportDoctorGuardBreakdown,
  BillingReportDoctorDecision,
  BillingReportDoctorEntryDetail
} from '../../types/api'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(value || 0)

const formatNumber = (value: number) =>
  new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 }).format(value || 0)

const formatCompactCurrency = (value: number) =>
  new Intl.NumberFormat('ro-RO', { notation: 'compact', maximumFractionDigits: 1 }).format(value || 0)

const BillingReportsPage: React.FC = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'institutions' | 'guard-reports'>('institutions')
  const [guardReportsSubTab, setGuardReportsSubTab] = useState<'institutions' | 'doctors'>('institutions')
  const [billingSubTab, setBillingSubTab] = useState<'per-hour' | 'per-segment'>('per-hour')
  const [segmentReportsSubTab, setSegmentReportsSubTab] = useState<'institutions' | 'doctors'>('institutions')

  const today = useMemo(() => new Date().toISOString().split('T')[0], [])
  const defaultFrom = useMemo(() => {
    const d = new Date()
    d.setDate(1)
    return d.toISOString().split('T')[0]
  }, [])

  const [fromDate, setFromDate] = useState(defaultFrom)
  const [toDate, setToDate] = useState(today)

  const [institutionFilter, setInstitutionFilter] = useState('')
  const [institutionStatus, setInstitutionStatus] = useState('')
  const [medicStatus, setMedicStatus] = useState('')
  const [medicFilter, setMedicFilter] = useState('')

  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [medics, setMedics] = useState<UserWithProfile[]>([])

  const [institutionSummary, setInstitutionSummary] = useState<BillingReportInstitutionResponse | null>(null)
  const [doctorSummary, setDoctorSummary] = useState<BillingReportDoctorResponse | null>(null)
  const [cycles, setCycles] = useState<BillingReportInstitutionClosedMonth[]>([])
  const [_selectedMedicEntry, setSelectedMedicEntry] = useState<string | null>(null)
  const [doctorEntryDetail, setDoctorEntryDetail] = useState<BillingReportDoctorEntryDetail | null>(null)
  const [showMedicDetail, setShowMedicDetail] = useState(false)

  const [institutionsLoading, setInstitutionsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadStaticData = async () => {
      try {
        const [institutionsData, medicsData] = await Promise.all([
          institutionService.getInstitutions({ limit: 200 }),
          userService.getUsers({ role: 'medic', limit: 200 })
        ])
        setInstitutions(institutionsData.institutions)
        setMedics(medicsData.users)
      } catch (err) {
        console.error('Error loading dropdown data', err)
        setError('Eroare la încărcarea listelor de instituții/medici')
      }
    }

    loadStaticData()
  }, [])

  const loadInstitutionReports = async () => {
    try {
      setError(null)
      setInstitutionsLoading(true)
      const response = await billingApi.getBillingReportsInstitutions({
        from_date: fromDate,
        to_date: toDate,
        institution_id: institutionFilter || undefined,
        status: institutionStatus || undefined
      })
      setInstitutionSummary(response)
      setCycles(response.closed_months)
    } catch (err) {
      console.error('Error loading institution reports', err)
      setError('Eroare la încărcarea raportului de instituții')
    } finally {
      setInstitutionsLoading(false)
    }
  }

  const loadMedicReports = async () => {
    try {
      setError(null)
      const response = await billingApi.getBillingReportsMedics({
        from_date: fromDate,
        to_date: toDate,
        institution_id: institutionFilter || undefined,
        doctor_id: medicFilter || undefined,
        status: medicStatus || undefined
      })
      setDoctorSummary(response)
    } catch (err) {
      console.error('Error loading medic reports', err)
      setError('Eroare la încărcarea raportului de medici')
    } finally {
    }
  }

  useEffect(() => {
    if (activeTab === 'institutions') {
      loadInstitutionReports()
    } else {
      loadMedicReports()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fromDate, toDate, institutionFilter, institutionStatus, medicFilter, medicStatus])

  if (!user || !['admin', 'operator'].includes(user.role)) {
    return (
      <Alert severity="error">Nu aveți permisiuni pentru a accesa rapoartele de billing.</Alert>
    )
  }

  const renderFilters = (isMedicTab: boolean) => (
    <Paper sx={{ p: 1.5, mb: 2 }}>
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            fullWidth
            label="De la"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            fullWidth
            label="Până la"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
            <InputLabel>Instituție</InputLabel>
            <Select
              label="Instituție"
              value={institutionFilter}
              onChange={(e) => setInstitutionFilter(e.target.value)}
            >
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate instituțiile</MenuItem>
              {institutions.map((inst) => (
                <MenuItem key={inst.id} value={inst.id} sx={{ fontSize: '0.8rem' }}>
                  {inst.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {isMedicTab && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel>Medic</InputLabel>
              <Select
                label="Medic"
                value={medicFilter}
                onChange={(e) => setMedicFilter(e.target.value)}
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toți medicii</MenuItem>
                {medics.map((medic) => (
                  <MenuItem key={medic.id} value={medic.id} sx={{ fontSize: '0.8rem' }}>
                    {medic.doctor_profile?.first_name} {medic.doctor_profile?.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={6} sm={4} md={2}>
          <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={isMedicTab ? medicStatus : institutionStatus}
              onChange={(e) => (isMedicTab ? setMedicStatus(e.target.value) : setInstitutionStatus(e.target.value))}
            >
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
              <MenuItem value="draft" sx={{ fontSize: '0.8rem' }}>În așteptare</MenuItem>
              <MenuItem value="approved" sx={{ fontSize: '0.8rem' }}>Aprobate</MenuItem>
              <MenuItem value="rejected" sx={{ fontSize: '0.8rem' }}>Respinse</MenuItem>
              <MenuItem value="validated" sx={{ fontSize: '0.8rem' }}>Validate</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />}
            onClick={isMedicTab ? loadMedicReports : loadInstitutionReports}
            fullWidth
            sx={{ fontSize: '0.78rem', py: 0.75 }}
          >
            Actualizează
          </Button>
        </Grid>
      </Grid>
    </Paper>
  )

  const statusLabels: Record<string, string> = {
    generated: 'Generat',
    draft: 'Draft',
    approved: 'Aprobat',
    rejected: 'Respins',
    validated: 'Validat'
  }

  const renderStatusAggregate = (aggregates?: Record<string, BillingReportStatusAggregate>) => {
    if (!aggregates) return []
    const ordering = ['generated', 'draft', 'approved', 'rejected', 'validated']
    return ordering
      .filter((key) => aggregates[key])
      .map((key) => {
        const value = aggregates[key]
        return {
          status: key,
          statusLabel: statusLabels[key] || key.toUpperCase(),
          entries: value.entries,
          amount: value.amount || 0,
          hours: value.hours || 0
        }
      })
  }

  const renderInstitutionPerHourContent = () => (
    <Box>
      {renderFilters(false)}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {renderStatusAggregate(institutionSummary?.totals).map((item) => (
          <Grid key={item.status} item xs={6} sm={3}>
            <Card sx={{ '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } } }}>
              <CardContent>
                <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">{item.statusLabel}</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }} color="primary">
                  {formatCurrency(item.amount)}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem' }} color="text.secondary">
                  {item.entries} înreg. • {formatNumber(item.hours)} ore
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{
        '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 },
        '& .MuiTableCell-root': { py: 0.5, px: 1 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <BusinessIcon color="primary" sx={{ fontSize: '1.2rem' }} />
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Instituții</Typography>
        </Box>
        {institutionsLoading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.900',
                '& th': {
                  color: 'common.white',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  py: 1,
                  px: 1.5,
                  whiteSpace: 'nowrap'
                }
              }}>
                <TableCell>Instituție</TableCell>
                <TableCell align="right">Generat</TableCell>
                <TableCell align="right">Draft</TableCell>
                <TableCell align="right">Aprobat</TableCell>
                <TableCell align="right">Validat</TableCell>
                <TableCell align="right">Ore gărzi</TableCell>
                <TableCell align="right">Cost gărzi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!institutionsLoading && (institutionSummary?.summaries?.length || 0) === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nu există date pentru filtrele selectate.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                institutionSummary?.summaries?.map((item) => {
                  const status = item.status_distribution || {}
                  return (
                    <TableRow key={item.institution_id || item.institution_name}>
                      <TableCell>{item.institution_name}</TableCell>
                      <TableCell align="right">{formatCurrency(status.generated?.amount || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(status.draft?.amount || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(status.approved?.amount || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(status.validated?.amount || 0)}</TableCell>
                      <TableCell align="right">{formatNumber(status.generated?.hours || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(
                        (item.guard_breakdown || []).reduce((acc, guard) => acc + guard.total_amount, 0)
                      )}</TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {cycles.length > 0 && (
        <Paper sx={{ mt: 2, '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 }, '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Luni închise</Typography>
            <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
              Perioade validate manual în sistem
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{
                  bgcolor: 'grey.900',
                  '& th': {
                    color: 'common.white',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    py: 1,
                    px: 1.5,
                    whiteSpace: 'nowrap'
                  }
                }}>
                  <TableCell>Perioadă</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell align="right">Înregistrări</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Validat de</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cycles.map((cycle) => (
                  <TableRow key={`${cycle.period}-${cycle.category}`}>
                    <TableCell>{cycle.period}</TableCell>
                    <TableCell>{cycle.category === 'institution' ? 'Instituție' : 'Medici'}</TableCell>
                    <TableCell align="right">{cycle.total_entries}</TableCell>
                    <TableCell align="right">{formatCurrency(cycle.total_amount)}</TableCell>
                    <TableCell>{cycle.closed_by_name || cycle.closed_by_id}</TableCell>
                    <TableCell>{new Date(cycle.closed_at).toLocaleString()}</TableCell>
                    <TableCell>{cycle.notes || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {institutionSummary?.timeline && institutionSummary.timeline.length > 0 && (
        <Paper sx={{ mt: 2, '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 }, '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
          <Box sx={{ p: 1.5 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Evoluție lunară instituții</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{
                  bgcolor: 'grey.900',
                  '& th': {
                    color: 'common.white',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    py: 1,
                    px: 1.5,
                    whiteSpace: 'nowrap'
                  }
                }}>
                  <TableCell>Perioadă</TableCell>
                  <TableCell align="right">Generat</TableCell>
                  <TableCell align="right">Draft</TableCell>
                  <TableCell align="right">Aprobat</TableCell>
                  <TableCell align="right">Respins</TableCell>
                  <TableCell align="right">Validat</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {institutionTimelineChartData.map((point) => (
                  <TableRow key={point.period}>
                    <TableCell>{new Date(point.period).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</TableCell>
                    <TableCell align="right">{formatCurrency(point.generated)}</TableCell>
                    <TableCell align="right">{formatCurrency(point.draft)}</TableCell>
                    <TableCell align="right">{formatCurrency(point.approved)}</TableCell>
                    <TableCell align="right">{formatCurrency(point.rejected)}</TableCell>
                    <TableCell align="right">{formatCurrency(point.validated)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  )

  // Sub-tab styling
  const subTabsSx = {
    minHeight: 28,
    borderBottom: 1,
    borderColor: 'divider',
    mb: 1.5,
    '& .MuiTab-root': {
      minHeight: 28,
      py: 0.25,
      px: 1,
      fontSize: '0.75rem',
      textTransform: 'none',
      '& .MuiSvgIcon-root': {
        fontSize: '0.85rem',
        mr: 0.5
      }
    }
  }

  const renderInstitutionTab = () => (
    <Box>
      <Tabs
        value={billingSubTab}
        onChange={(_, newValue) => setBillingSubTab(newValue)}
        sx={subTabsSx}
      >
        <Tab
          icon={<ScheduleIcon />}
          iconPosition="start"
          label="Per Oră"
          value="per-hour"
        />
        <Tab
          icon={<AssessmentIcon />}
          iconPosition="start"
          label="Per Segmente"
          value="per-segment"
        />
      </Tabs>

      {billingSubTab === 'per-hour' && renderInstitutionPerHourContent()}
      {billingSubTab === 'per-segment' && (
        <Box>
          <Tabs
            value={segmentReportsSubTab}
            onChange={(_, newValue) => setSegmentReportsSubTab(newValue)}
            sx={subTabsSx}
          >
            <Tab
              icon={<BusinessIcon />}
              iconPosition="start"
              label="Instituție"
              value="institutions"
            />
            <Tab
              icon={<PeopleIcon />}
              iconPosition="start"
              label="Medici"
              value="doctors"
            />
          </Tabs>
          <SegmentReportsTab activeSubTab={segmentReportsSubTab} />
        </Box>
      )}
    </Box>
  )

  const institutionTimelineChartData = useMemo(() => (
    institutionSummary?.timeline
      ? institutionSummary.timeline.map((point) => ({
          period: point.period,
          generated: point.generated,
          draft: point.draft,
          approved: point.approved,
          rejected: point.rejected,
          validated: point.validated
        }))
      : []
  ), [institutionSummary])

  const medicTimelineChartData = useMemo(() => (
    doctorSummary?.timeline
      ? doctorSummary.timeline.map((point) => ({
          period: point.period,
          generated: point.generated,
          draft: point.draft,
          approved: point.approved,
          rejected: point.rejected,
          validated: point.validated
        }))
      : []
  ), [doctorSummary])

  const handleMedicEntryClick = async (entryId: string) => {
    try {
      const detail = await billingApi.getBillingMedicEntry(entryId)
      setDoctorEntryDetail(detail)
      setSelectedMedicEntry(entryId)
      setShowMedicDetail(true)
    } catch (err) {
      console.error('Error loading medic entry detail', err)
      setError('Nu s-au putut încărca detaliile înregistrării')
    }
  }

  const closeMedicDetail = () => {
    setShowMedicDetail(false)
    setDoctorEntryDetail(null)
    setSelectedMedicEntry(null)
  }

  const medicTotalsCards = useMemo(() => {
    const aggregates = doctorSummary?.status_distribution
    if (!aggregates) return []
    const order = ['generated', 'draft', 'approved', 'rejected', 'validated']
    return order
      .filter((key) => aggregates[key])
      .map((key) => ({
        key,
        label: statusLabels[key] || key.toUpperCase(),
        amount: aggregates[key].amount,
        entries: aggregates[key].entries,
        hours: aggregates[key].hours,
        segments: aggregates[key].segments
      }))
  }, [doctorSummary])

  const renderMedicsTab = () => (
    <Box>
      {renderFilters(true)}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {medicTotalsCards.map((item) => (
          <Grid key={item.key} item xs={6} sm={4} md={2}>
            <Card sx={{ '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } } }}>
              <CardContent>
                <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">{item.label}</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }} color="primary">
                  {formatCurrency(item.amount)}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem' }} color="text.secondary">
                  {item.entries} înreg. • {formatNumber(item.hours || 0)}h • {formatNumber(item.segments || 0)} seg
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {medicTimelineChartData.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Evoluție lunară medici</Typography>
            <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
              Facturat vs plătit vs restanțe
            </Typography>
          </Box>
          <Box sx={{ height: 280, px: 1.5, py: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={medicTimelineChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis tickFormatter={formatCompactCurrency} width={80} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Line type="monotone" dataKey="generated" name="Generat" stroke="#0288d1" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="draft" name="Draft" stroke="#f57c00" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="approved" name="Aprobat" stroke="#2e7d32" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="rejected" name="Respins" stroke="#c2185b" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="validated" name="Validat" stroke="#7b1fa2" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}

      {doctorSummary?.segments?.length ? (
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Segmente per medic</Typography>
            <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
              Distribuție pe segmente
            </Typography>
          </Box>
          <Box sx={{ height: 280, px: 1.5, py: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={doctorSummary.segments}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="segment_code" />
                <YAxis tickFormatter={formatCompactCurrency} width={80} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="generated_amount" name="Generat" fill="#1976d2" radius={[4, 4, 0, 0]} />
                <Bar dataKey="approved_amount" name="Aprobat" fill="#2e7d32" radius={[4, 4, 0, 0]} />
                <Bar dataKey="rejected_amount" name="Respins" fill="#c2185b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      ) : null}

      {doctorSummary?.guards?.length ? (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Evoluție lunară medici</Typography>
            <Typography variant="body2" color="text.secondary">
              Facturat vs plătit vs restanțe pentru medici
            </Typography>
          </Box>
          <Box sx={{ height: 320, px: 2, py: 3 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={doctorSummary.guards}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="shift_type" />
                <YAxis tickFormatter={formatCompactCurrency} width={80} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="total_amount" name="Cost total" fill="#9c27b0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      ) : null}

      {doctorSummary?.recent_decisions?.length ? (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Decizii recente</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Medic</TableCell>
                  <TableCell>Instituție</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Sumă</TableCell>
                  <TableCell>Decis de</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell align="right">Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {doctorSummary.recent_decisions.map((decision: BillingReportDoctorDecision) => (
                  <TableRow key={decision.entry_id}>
                    <TableCell>{decision.doctor_name || decision.doctor_id}</TableCell>
                    <TableCell>{decision.institution_name || '—'}</TableCell>
                    <TableCell>{decision.status}</TableCell>
                    <TableCell align="right">{formatCurrency(decision.amount)}</TableCell>
                    <TableCell>{decision.decided_by || '—'}</TableCell>
                    <TableCell>{new Date(decision.decided_at).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => handleMedicEntryClick(decision.entry_id)}>Detalii</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      {doctorSummary?.summaries?.length ? (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon color="primary" />
            <Typography variant="h6">Sumar medici</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Medic</TableCell>
                  <TableCell align="right">Generat</TableCell>
                  <TableCell align="right">Draft</TableCell>
                  <TableCell align="right">Aprobat</TableCell>
                  <TableCell align="right">Respins</TableCell>
                  <TableCell align="right">Validat</TableCell>
                  <TableCell align="right">Segmente</TableCell>
                  <TableCell align="right">Ore gărzi</TableCell>
                  <TableCell align="right">Cost gărzi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {doctorSummary.summaries.map((summary: BillingReportDoctorSummary) => {
                  const status = summary.status_distribution || {}
                  return (
                    <TableRow key={summary.doctor_id}>
                      <TableCell>{summary.doctor_name}</TableCell>
                      <TableCell align="right">{formatCurrency(status.generated?.amount || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(status.draft?.amount || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(status.approved?.amount || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(status.rejected?.amount || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(status.validated?.amount || 0)}</TableCell>
                      <TableCell align="right">{formatNumber(status.generated?.segments || 0)}</TableCell>
                      <TableCell align="right">{formatNumber(status.generated?.hours || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(
                        (summary.guard_breakdown || []).reduce((acc, guard) => acc + guard.total_amount, 0)
                      )}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      {showMedicDetail && doctorEntryDetail && (
        <Paper sx={{ mb: 3, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Detalii înregistrare</Typography>
            <Button onClick={closeMedicDetail}>Închide</Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Medic</Typography>
              <Typography variant="body1">{doctorEntryDetail.doctor_name || doctorEntryDetail.doctor_id}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Instituție</Typography>
              <Typography variant="body1">{doctorEntryDetail.institution_name || doctorEntryDetail.institution_id || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Status</Typography>
              <Typography variant="body1">{doctorEntryDetail.status}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Tip</Typography>
              <Typography variant="body1">{doctorEntryDetail.billing_type === 'hour' ? 'Gardă' : 'Segment'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Cantitate</Typography>
              <Typography variant="body1">{formatNumber(doctorEntryDetail.quantity)}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Sumă</Typography>
              <Typography variant="body1">{formatCurrency(doctorEntryDetail.amount)}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Detalii</Typography>
              <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>{JSON.stringify(doctorEntryDetail.details, null, 2)}</pre>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary">Aprobat de</Typography>
              <Typography variant="body1">{doctorEntryDetail.approved_by || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary">Validat de</Typography>
              <Typography variant="body1">{doctorEntryDetail.validated_by || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary">Plătit de</Typography>
              <Typography variant="body1">{doctorEntryDetail.paid_by || '—'}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  )

  const renderGuardReportsTab = () => (
    <Box>
      <Tabs
        value={guardReportsSubTab}
        onChange={(_, newValue) => setGuardReportsSubTab(newValue)}
        sx={subTabsSx}
      >
        <Tab
          icon={<BusinessIcon />}
          iconPosition="start"
          label="Instituții"
          value="institutions"
        />
        <Tab
          icon={<PeopleIcon />}
          iconPosition="start"
          label="Medici"
          value="doctors"
        />
      </Tabs>

      {guardReportsSubTab === 'institutions' ? (
        <GuardReportsInstitutionTab />
      ) : (
        <GuardReportsDoctorTab />
      )}
    </Box>
  )

  // Compact tab styling consistent cu worklist
  const compactTabsSx = {
    minHeight: 32,
    borderBottom: 1,
    borderColor: 'divider',
    mb: 2,
    '& .MuiTab-root': {
      minHeight: 32,
      py: 0.5,
      px: 1.5,
      fontSize: '0.78rem',
      textTransform: 'none',
      '& .MuiSvgIcon-root': {
        fontSize: '0.95rem',
        mr: 0.5
      }
    }
  }

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={compactTabsSx}
      >
        <Tab
          icon={<AssessmentIcon />}
          iconPosition="start"
          label="Rapoarte Billing"
          value="institutions"
        />
        <Tab
          icon={<ScheduleIcon />}
          iconPosition="start"
          label="Rapoarte Gardă"
          value="guard-reports"
        />
      </Tabs>

      {activeTab === 'institutions' && renderInstitutionTab()}
      {activeTab === 'guard-reports' && renderGuardReportsTab()}
    </Box>
  )
}

export default BillingReportsPage

